import { FC, useState } from 'react';

import { router } from '@inertiajs/react';

import { SellerCard } from './SellerCard';
import { SellerUniverseFilter } from './SellerUniverseFilter';
import { DebouncedSearchInput } from '../../components/shared/DebouncedSearchInput';
import { InfiniteScrollTrigger } from '../../components/shared/InfiniteScrollTrigger';
import { Routes } from '../../models/routes';
import { Seller, Universe } from '../../types';

interface Props {
    sellers: Seller[];
    sellersCount: number;
    nextPage: number | null;
    sellerUniverses: Pick<Universe, 'id' | 'name' | 'slug'>[];
    selectedUniverseSlug: string | null;
    searchQuery: string;
}

const SellersIndex: FC<Props> = ({
    sellers,
    sellersCount,
    nextPage,
    sellerUniverses,
    selectedUniverseSlug,
    searchQuery,
}) => {
    const [loading, setLoading] = useState(false);

    const loadMore = () => {
        if (!nextPage || loading) return;

        router.get(
            Routes.Sellers,
            {
                page: nextPage,
                ...(selectedUniverseSlug
                    ? { universe: selectedUniverseSlug }
                    : {}),
                ...(searchQuery ? { search: searchQuery } : {}),
            },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['sellers', 'nextPage'],
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };

    const searchSellers = (nextQuery: string) => {
        router.get(
            Routes.Sellers,
            {
                ...(selectedUniverseSlug
                    ? { universe: selectedUniverseSlug }
                    : {}),
                ...(nextQuery ? { search: nextQuery } : {}),
            },
            {
                preserveScroll: true,
                preserveState: true,
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };

    return (
        <>
            <section className="bg-muted/30 py-4">
                <div className="container flex items-baseline gap-2">
                    <h1 className="text-xl font-bold md:text-2xl">Продавці</h1>
                    <p className="text-sm text-muted-foreground">
                        • Знайдено продавців: {sellersCount}
                    </p>
                </div>
            </section>

            <section className="pb-2.5">
                <div className="container px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between">
                        <div className="min-w-0 flex-1">
                            <SellerUniverseFilter
                                universes={sellerUniverses}
                                selectedUniverseSlug={selectedUniverseSlug}
                                searchQuery={searchQuery}
                            />
                        </div>
                        <DebouncedSearchInput
                            query={searchQuery}
                            loading={loading}
                            onSearch={searchSellers}
                            placeholder="Пошук продавців"
                            inputTestId="sellers-search-input"
                            className="w-full md:max-w-sm md:shrink-0"
                            inputClassName="h-9 bg-background text-base md:text-sm"
                        />
                    </div>
                </div>
            </section>

            <section className="pb-6">
                <div className="container mt-3 px-4 sm:px-6 lg:px-8">
                    {sellers.length > 0 ? (
                        <div
                            className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
                            data-testid="sellers-grid"
                        >
                            {sellers.map((seller) => (
                                <SellerCard key={seller.id} seller={seller} />
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-dashed bg-card px-6 py-14 text-center">
                            <h2 className="text-xl font-semibold">
                                {searchQuery
                                    ? 'Продавців не знайдено'
                                    : 'Активних продавців поки немає'}
                            </h2>
                            <p className="mt-2 text-sm text-muted-foreground">
                                {searchQuery
                                    ? `За запитом «${searchQuery}» немає активних продавців.`
                                    : 'Тут з’являться продавці, щойно вони опублікують пропозиції.'}
                            </p>
                        </div>
                    )}

                    {nextPage && (
                        <InfiniteScrollTrigger
                            className="mt-8"
                            loading={loading}
                            onLoadMore={loadMore}
                        />
                    )}
                </div>
            </section>
        </>
    );
};

export default SellersIndex;
